import { z } from "zod";

// Flexible phone validation: digits only, 4-15 digits (international range)
const PHONE_REGEX = /^\d{4,15}$/;

// Zod schema for customer form validation
export const customerSchema = z.object({
  name: z
    .string()
    .min(1, "Firma adı zorunludur")
    .min(2, "Firma adı en az 2 karakter olmalıdır"),

  email: z
    .string()
    .email("Geçerli bir e-posta adresi giriniz")
    .optional()
    .or(z.literal("")),

  phone: z
    .string()
    .min(1, "Telefon numarası zorunludur")
    .regex(PHONE_REGEX, "Geçerli bir telefon numarası giriniz (4-15 rakam)"),

  address: z
    .string()
    .min(1, "Adres zorunludur")
    .min(10, "Adres en az 10 karakter olmalıdır"),

  city: z
    .string()
    .min(1, "Şehir zorunludur"),

  ilce: z
    .string()
    .optional()
    .or(z.literal("")),

  primaryPeople: z
    .string()
    .min(1, "Birincil yetkili kişi zorunludur")
    .refine(
      (val) => val.trim().split(/\s+/).length >= 2,
      "Lütfen geçerli bir Ad ve Soyad giriniz (En az 2 kelime)."
    ),

  primaryPeoplePos: z
    .string()
    .min(1, "Birincil yetkili pozisyonu zorunludur"),

  primaryPeoplePhone: z
    .string()
    .min(1, "Birincil yetkili telefonu zorunludur")
    .regex(PHONE_REGEX, "Geçerli bir telefon numarası giriniz (4-15 rakam)"),

  primaryPeopleEmail: z
    .string()
    .email("Geçerli bir e-posta adresi giriniz")
    .optional()
    .or(z.literal("")),

  secondaryPeople: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.trim().split(/\s+/).length >= 2,
      "Lütfen geçerli bir Ad ve Soyad giriniz (En az 2 kelime)."
    )
    .or(z.literal("")),

  secondaryPeoplePos: z
    .string()
    .optional()
    .or(z.literal("")),

  secondaryPeoplePhone: z
    .string()
    .regex(PHONE_REGEX, "Geçerli bir telefon numarası giriniz (4-15 rakam)")
    .optional()
    .or(z.literal("")),

  secondaryPeopleEmail: z
    .string()
    .email("Geçerli bir e-posta adresi giriniz")
    .optional()
    .or(z.literal("")),

  customerStatus: z
    .string()
    .min(1, "Müşteri durumu zorunludur"),

  notes: z
    .string()
    .optional()
    .or(z.literal("")),
});

/**
 * Validate customer form data against schema
 * @param {Object} formData - Form data to validate
 * @returns {{ success: boolean, errors: Object|null, data: Object|null }}
 */
export const validateCustomerForm = (formData) => {
  const result = customerSchema.safeParse(formData);

  if (result.success) {
    return { success: true, errors: null, data: result.data };
  }

  // Convert Zod errors to a flat object keyed by field name
  const errors = {};
  result.error.issues.forEach((issue) => {
    const fieldName = issue.path[0];
    if (!errors[fieldName]) {
      errors[fieldName] = issue.message;
    }
  });

  console.error("Zod validation errors:", errors);
  return { success: false, errors, data: null };
};

/**
 * Get the first validation error message
 * @param {Object} errors - Errors object from validateCustomerForm
 * @returns {string|null} First error message or null
 */
export const getFirstError = (errors) => {
  if (!errors) return null;
  const firstKey = Object.keys(errors)[0];
  return firstKey ? errors[firstKey] : null;
};
